import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save } from "lucide-react";

const PLAN_OPTIONS = [
  { value: "none", label: "No plan (free account)" },
  { value: "starter", label: "Starter ($39/mo)" },
  { value: "pro", label: "Pro ($99/mo)" },
  { value: "enterprise", label: "Enterprise ($299/mo)" },
];

export default function EditUserDialog({ user, open, onOpenChange, onSaved }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    company: "",
    role: "employer",
    plan: "none",
    grant_days: "",
    password: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        company: user.company || "",
        role: user.role || "employer",
        plan: user.subscription_plan || "none",
        grant_days: "",
        password: "",
      });
    }
  }, [user]);

  if (!user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {};
      if (form.name !== (user.name || "")) payload.name = form.name;
      if (form.company !== (user.company || "")) payload.company = form.company;
      if (form.role !== (user.role || "employer")) payload.role = form.role;
      const currentPlan = user.subscription_plan || "none";
      if (form.plan !== currentPlan) {
        payload.plan = form.plan;
        if (form.plan !== "none") {
          payload.grant_days = parseInt(form.grant_days, 10) || 365;
        }
      } else if (form.grant_days && form.plan !== "none") {
        payload.grant_days = parseInt(form.grant_days, 10) || 365;
      }
      if (form.password && form.password.length >= 6) {
        payload.password = form.password;
      }
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setSubmitting(false);
        return;
      }
      await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-user-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            Edit user
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">{user.email}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="edit-user-name">Full name</Label>
            <Input
              id="edit-user-name"
              data-testid="edit-user-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-user-company">Company</Label>
            <Input
              id="edit-user-company"
              data-testid="edit-user-company"
              value={form.company}
              onChange={(e) =>
                setForm({ ...form, company: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm({ ...form, role: v })}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employer">Employer</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Plan</Label>
              <Select
                value={form.plan}
                onValueChange={(v) => setForm({ ...form, plan: v })}
              >
                <SelectTrigger data-testid="edit-user-plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLAN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.plan !== "none" && (
            <div className="grid gap-2">
              <Label htmlFor="edit-user-days">
                Free access duration (days)
              </Label>
              <Input
                id="edit-user-days"
                data-testid="edit-user-grant-days"
                type="number"
                min="1"
                placeholder={
                  user.subscription_active_until
                    ? `Currently active until ${new Date(
                        user.subscription_active_until
                      ).toLocaleDateString()}`
                    : "e.g. 365"
                }
                value={form.grant_days}
                onChange={(e) =>
                  setForm({ ...form, grant_days: e.target.value })
                }
              />
              <p className="text-xs text-slate-500">
                Leave blank to keep current expiry. Setting a value will
                reset access to expire that many days from now.
              </p>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="edit-user-password">
              New password{" "}
              <span className="text-xs text-slate-400">
                (leave blank to keep current)
              </span>
            </Label>
            <Input
              id="edit-user-password"
              data-testid="edit-user-password"
              type="text"
              placeholder="Min 6 characters"
              value={form.password}
              onChange={(e) =>
                setForm({ ...form, password: e.target.value })
              }
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="edit-user-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="edit-user-submit"
              className="bg-brand-gradient text-white"
            >
              <Save className="mr-2 h-4 w-4" />
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
